// 168B — `react/react-in-jsx-scope` counts PER JSX ELEMENT, and 174 §4 adds one
// to this file. The import IS the rule's own remedy, and it clears all 136.
import React, { useState, useRef, useEffect, Fragment, lazy, Suspense } from "react";
import {
  // BRIEF 105 §3a — `File`, not `FileText`. See CaseTaskRow.tsx for the census.
  File,
  Archive,
  ChevronDown,
  ChevronUp,
  Calendar,
  MessageCircle,
  Phone,
  AlertTriangle,
  Gavel,
  Info,
  ExternalLink,
  ChevronRight,
  ChevronsUpDown,
  ChevronsDownUp,
  Grid2x2,
  X,
} from "lucide-react";
import { TaskMatrix } from "../TaskMatrix";
/* BRIEF 115 §§1-2 — both row families mount the canonical components
   (B.3: one renderer for one item). The 107 §2b note that stood here
   ("this view mounts NEITHER CaseTaskRow NOR CaseEventRow — it writes its
   own rows") is WITHDRAWN whole: both halves do now, and the hand-rolled
   row and card went with their imports (SubtaskChecklist, TaskDocsPane,
   SubtaskCounter/EventSubtasks, NotesDrawer, DateBlock, LinkIcon, SelfTick,
   the drawer icon pair, the eisen/urgency/archive helpers). */
import CaseTaskRow from "../components/CaseTaskRow";
// 174 §4 — the non-case-linked email desk. Its own file: the case panel's row
// is already duplicated once (CaseEmailsSection.tsx:56) and a third copy in
// this 2300-line view is not a thing to add.
/* 183 D.3 — THE DESK IS THE DRAWER. The dashboard's e-mail surface is the
   deep-search drawer's THIRD mount (CaseView, CollectiveCaseView, here),
   inline and matter-less: nothing below is written for the dashboard but the
   mount and its data. Lazy as CaseEmailsSection loads it, for the bundle. */
const EmailDeepSearchDrawer = lazy(() => import("../components/EmailDeepSearchDrawer"));
import { Mail } from "lucide-react";
import { periodBounds } from "../lib/periodRange";
import { emailLogTimePrefill, type EmailForTimeEntry } from "../lib/emailLogTimePrefill"; // 184 C.4
import { storedCaseLanguage } from "../lib/docLang.mjs"; // 209 §6 — the matter's language, for its time entry's words
/** An inline drawer has nothing to close; the prop is required. */
const NOOP = () => undefined;
import CaseEventRow from "../components/CaseEventRow";
// Phase 29 C60 — delegation-aware "my tasks": a task is mine when assigned
// to me OR carrying an open subtask assigned to me; the date I see/sort by
// is MY subtask's due date in the latter case (the owner keeps the parent
// date). Pure helpers shared with the compact column's selector.
import { taskIsMine, viewerDueDate } from "../lib/dueToday";
import { countTasks } from "../lib/taskCounters";
import { namesMe } from "../lib/mentionFilter"; // 67 §1 — the one definition
import {
  I,
  SK,
  sv,
  ctc,
  coIni,
  nid,
  TASK_STATUSES,
  itemLevelMap,
  queueScrollToEv,
  queueScrollToTk,
  eisenAuto,
  SCHED_DAYS,
  SCHED_START,
  SCHED_SLOTS,
  normalizeWeeklySchedule,
  slotToTime,
  firmNameSlug,
  dayStrip,
  schedDayIndex,
} from "../utils";
import {
  MyAllToggle,
  SegmentedControl,
  InitialsBadge,
  AddButton,
  ModalLayout,
  HScroll, // 133 §3
  OPEN_MODE_GLOW, OPEN_MODE_ICON, // 190 round 6 [AA] — the lock's glow
  ListNotLoaded, // 210 §1
} from "../components/SharedUI";
import { useListsNotLoaded } from "../lib/listLoad"; // 210 §1
import CaseTimeEntriesSection from "../components/CaseTimeEntriesSection"; // 150 §1 surface 4/4
import { usePermissions } from "../hooks/usePermissions";
import { useTaxonomy, useTaxonomyList } from "../hooks/useTaxonomy";
import { useRoleLabels } from "../hooks/useRoleLabels";
import { getScheduleOverrides } from "../api";
import {
  getWorkLocationMeta,
  resolveTaxonomyLabel,
} from "../lib/taxonomyHelpers";
import { resolveMatter } from "../lib/matterLabel"; // 191 §C
import { useTranslation } from "../i18n";
import { CT, RAD, FS, Z } from "../lib/tokens";
import EventTypeChips from "../components/EventTypeChips";
import { axisDayOf } from "../lib/scheduleAxis";
import { eventTypeVisible } from "../lib/eventTypeFilter";
import Table from "../components/Table";
import Pager from "../components/Pager";
import { UNDO_WINDOW_MS } from "../components/useConfirmAndUndo"; // 209 §25 — the two undo bars run the undo's own window
import { pageOf } from "../hooks/usePager";
import { renderMentions, chatTime } from "./ChatView"; // 206E §2.5 — the one mention renderer; §3 — the one relative time (207B §3 — its clock in italics)

// BUG-A SWEEP — hoisted out of Dash's render body. It was declared there and
// mounted as <CC …/>, so every Dashboard render gave React a new component type
// for each of these stat cards: the whole card subtree was discarded and
// rebuilt rather than updated. A pure move — it closes over nothing but the
// module-scope tokens.
/* 147 §1.3 — the twin of `matterListCfg.tsx`'s block, derived for the same
   reason: the floor was the literal `1172` beside a `minWidth: 80` and a
   `gap: 4`, and the literal is arithmetic on the other two. The 13px by which
   this floor exceeds the matter list's is REAL and not drift: 13 gaps × the
   1px by which this strip's gap is wider. The derivation is what makes that a
   consequence instead of a coincidence — change the gap here and the floor
   follows, which is exactly what did NOT happen in 132. */
/* 210 §1 — what the dashboard's blocks count and list: the tasks (five of the
   six cards, the task block), the time entries (the hours card; the time block
   says so itself — CaseTimeEntriesSection), the agenda (the event block).
   While one could not load, its block says so in its place (ListNotLoaded):
   never « 0 », never « no tasks ». */
const TK_LIST: readonly string[] = [SK.tk];
const AG_LIST: readonly string[] = [SK.ag];
const KPI_LISTS: readonly string[] = [SK.tk, SK.te];
const STRIP14_DAYS = 14;
const STRIP14_COL = 80;
const STRIP14_GAP = 4;
const STRIP14_MIN = STRIP14_DAYS * STRIP14_COL + (STRIP14_DAYS - 1) * STRIP14_GAP;

const CC = ({ val, label, color, children }: any) => (
  <div
    style={{
      background: "var(--bc)",
      border: "1px solid var(--br)",
      borderRadius: RAD.r10,
      padding: "10px 14px",
      textAlign: "center",
      flex: "1 1 0",
      minWidth: 88,
    }}
  >
    {children || (
      <div
        style={{
          fontSize: FS.s22,
          fontWeight: 700,
          fontFamily: "'JetBrains Mono',monospace",
          lineHeight: 1,
          color: color || "var(--tp)",
          marginBottom: 4,
        }}
      >
        {val}
      </div>
    )}
    <div
      style={{
        fontSize: FS.s9,
        color: "var(--tm)",
        textTransform: "uppercase",
        letterSpacing: ".4px",
        lineHeight: 1.3,
      }}
    >
      {label}
    </div>
  </div>
);

// ── BRIEF 163 §B.1 — THE MENTIONS IIFE BECOMES A CHILD COMPONENT ────────────
// It held a useState (msgP) inside an IIFE in Dash's JSX — a conditional hook
// by construction. The one shape of the fix: the IIFE that contains a hook
// becomes a child component, at module scope (Bug-A: stable identity), its
// closure passed as props. Body verbatim from the IIFE; `tr` now comes from
// this component's own useTranslation().
const PersonalMessagesCard = ({
  currentUser,
  co,
  ca,
  cc,
  chatMsgs,
  gChat,
  chatRead,
  nav,
  navCase,
  setChatOpen,
}: any) => {
  const { t: tr } = useTranslation();
  /* C63 — @mention matching follows the same initials source as the
     badges (stored initials win, name-derived fallback), so what the
     user SEES on badges is what an @mention must say. */
  const myIni = currentUser
    ? coIni(co.find((x: any) => x.id === currentUser))
    : "";
  const allMsgs = [
    ...Object.entries(chatMsgs).flatMap(([cid, msgs]: any) => {
      if (typeof cid === "string" && cid.startsWith("cc:")) {
        const ccId = parseInt(cid.slice(3), 10);
        return Number.isFinite(ccId)
          ? msgs.map((m: any) => ({ ...m, scope: "cc", ccId }))
          : [];
      }
      return msgs.map((m: any) => ({ ...m, scope: "case", caseId: +cid }));
    }),
    ...(Array.isArray(gChat)
      ? gChat.map((m) => ({ ...m, scope: "general" }))
      : []),
  ];
  /* 67 §1 — THE THIRD COPY. This read `m.text.includes("@" + myIni) ||
     m.text.includes("@all")`: the pre-65c substring test, still live here
     after brief 65 collapsed the two copies in App.tsx and brief 66 ruled
     the boundary on the module they became. So `@allan` still notified
     the whole firm in THIS list, `LA` still matched inside `@Laurent`,
     and an unresolved collaborator (myIni "") made every message
     carrying any `@` a mention. It now asks the one definition. */
  const isMention = (m: any) =>
    !!currentUser && namesMe(m.text, myIni);
  const lrKey = (m: any) =>
    m.scope === "cc"
      ? `cc:${m.ccId}:${currentUser}`
      : m.scope === "general"
        ? `gchat:${currentUser}`
        : `${m.caseId}:${currentUser}`;
  const [msgP, setMsgP] = useState({ open: true, mentions: true });
  const allSorted = [...allMsgs].sort((a, b) => b.ts - a.ts);
  const msgFilt = msgP.mentions
    ? allSorted.filter((m) => isMention(m))
    : allSorted;
  const msgShown = msgFilt.slice(0, 15);
  const unreadMsgN = currentUser
    ? allSorted.filter((m) => {
        const lr = chatRead[lrKey(m)] || 0;
        return m.ts > lr && m.author !== currentUser && isMention(m);
      }).length
    : 0;
  return (
    <div className="detail-card" style={{ marginBottom: 10 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: msgP.open ? 10 : 2,
        }}
      >
        <h4
          style={{ margin: 0, cursor: "pointer" }}
          onClick={() => setMsgP((x) => ({ ...x, open: !x.open }))}
        >
          {tr("dash.personal_messages")}
        </h4>
        {unreadMsgN > 0 && (
          <span
            style={{
              fontSize: FS.s8,
              fontWeight: 700,
              color: CT.c_fff,
              background: "var(--rd)",
              borderRadius: RAD.r8,
              padding: "0 4px",
              lineHeight: "14px",
              minWidth: 14,
              textAlign: "center",
              display: "inline-block",
            }}
          >
            {unreadMsgN}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            cursor: "pointer",
            color: "var(--tm)",
          }}
          onClick={() => setMsgP((x) => ({ ...x, open: !x.open }))}
        >
          {msgP.open ? (
            <ChevronUp size={11} />
          ) : (
            <ChevronDown size={11} />
          )}
        </span>
      </div>
      {msgP.open && (
        <>
          {msgShown.length === 0 && (
            <p
              style={{
                fontSize: FS.s11,
                color: "var(--tm)",
                margin: "4px 0",
              }}
            >
              {msgP.mentions ? tr("dash.no_mentions") : tr("chat.empty")}
            </p>
          )}
          {msgShown.map((m) => {
            const mc = co.find((c: any) => c.id === m.author);
            const ini3 = mc ? coIni(mc) : "?";
            const isMe = m.author === currentUser;
            const cs2 =
              m.scope === "case"
                ? ca.find((c: any) => c.id === m.caseId)
                : null;
            const cc2 =
              m.scope === "cc"
                ? (cc || []).find((c: any) => c.id === m.ccId)
                : null;
            const mentioned = isMention(m);
            const lr = chatRead[lrKey(m)] || 0;
            const isUnread =
              currentUser && m.ts > lr && m.author !== currentUser;
            const goTo = () => {
              /* Round 19 (C104) — chat rows arrive over SSE with no
                 visibility filter, so a message can reference a
                 case/cc OUTSIDE the viewer's scoped lists; the raw
                 jump landed on a blank view whose chronicle fetch
                 then 403'd. C85 guard on both branches. */
              if (m.scope === "cc") {
                navCase("cc", m.ccId);
              } else if (m.scope === "general") {
                nav("gchat");
              } else {
                if (navCase("ca", m.caseId)) setChatOpen(true);
              }
            };
            const parentTitle = cs2
              ? cs2.title
              : cc2
                ? cc2.title
                : m.scope === "general"
                  ? tr("nav.general_chat", { fallback: "General chat" })
                  : "";
            const rowKey =
              m.id +
              "-" +
              (m.scope === "cc"
                ? "cc:" + m.ccId
                : m.scope === "general"
                  ? "g"
                  : m.caseId);
            return (
              <div
                key={rowKey}
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "flex-start",
                  padding: "6px 0 6px 6px",
                  borderBottom: "1px solid rgba(255,255,255,.04)",
                  borderLeft: mentioned
                    ? "2px solid var(--ac)"
                    : "2px solid transparent",
                  cursor: "pointer",
                  background: isUnread ? "rgba(74,124,255,.04)" : "none",
                }}
                onClick={goTo}
              >
                <InitialsBadge
                  person={mc}
                  isMe={isMe}
                  size={18}
                  fontSize={8}
                  style={{ marginTop: 1 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* 207C §3 [RULED 2026-09-24, ruling 7] — THE MESSAGE FIRST, beside
                      the badge, its two-line clamp kept; UNDER IT its time and, after a
                      « · », the chat it was written in — the matter's title (the
                      General chat's name for the general chat), clickable as before, in
                      the accent colour, without the pill's frame; the time at that
                      name's size. It was the pill, then the time pushed right, above
                      the message. */}
                  <div
                    style={{
                      fontSize: FS.s10_5,
                      color: "var(--tp)",
                      lineHeight: 1.4,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                    }}
                  >
                    {/* 206E §2.5 — the FOURTH copy of the mention renderer (the brief's
                        census named three), moved to ChatView's one `renderMentions`;
                        the reader passed, so his own mention is green here too, as in
                        every chat; §3 — and `@all` is the chats' own `chat.mention_all`
                        (it was `case.mention.all_collaborators`, the same words). */}
                    {renderMentions(m.text, co, currentUser, String(tr("chat.mention_all", { fallback: "All collaborators" })))}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      marginTop: 2,
                      fontSize: FS.s9_5,
                      color: "var(--tm)",
                      minWidth: 0,
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>
                      {chatTime(m.ts, tr) /* 206E §3 — the one relative time; 207B §3 — its clock in italics */}
                    </span>
                    {parentTitle && (
                      <>
                        <span style={{ flexShrink: 0 }}>·</span>
                        <span
                          style={{ color: "var(--ac)", cursor: "pointer", flexShrink: 0 }}
                          title={parentTitle}
                          onClick={(e2) => {
                            e2.stopPropagation();
                            goTo();
                          }}
                        >
                          {/* BRIEF 102 §1a — KEPT, §7's first stop; the
                              `.lib-pill` reasoning at :1226. */}
                          {parentTitle.length > 20
                            ? parentTitle.slice(0, 20) + "…"
                            : parentTitle}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

const Dash = ({
  ca,
  co,
  te,
  sTe,
  ag,
  sAg,
  tk,
  sTk,
  tkTypes,
  evtTypes,
  schedAxis,
  firmSettings, // 198 §C — `periodBounds`' axis input for the e-mail desk and the time-entry table (D.3)
  holidays, // 132 §6
  calDisp, // 133 §4
  agTypeFilt,
  setAgTypeFilt,
  currentUser,
  showDL,
  nwh,
  dTeP,
  setDTeP,
  tePeriod,
  setTePeriod,
  activeTimer,
  activeTimers,
  chatMsgs,
  gChat,
  chatRead,
  setChatOpen,
  setTkDelConfirm,
  s,
  nav,
  om,
  toast,
  etTag,
  evtLongestCode, // 139 §1 — travels with etTag, to CaseEventRow
  coAvs,
  locIcon,
  svTkData,
  delTk,
  TkStatusTag,
  startTimer,
  getTimerDisplay,
  evToVevent,
  downloadIcs,
  keepScroll,
  DLBtn,
  OPEN_ST,
  setEisenOpen,
  userMatrix,
  userMatrixSetQuadrant,
  cc,
  /* 174 §4 — the email desk's matter picker reads the same recents every
     other MatterPicker host does. */
  getRecentCases,
  /* 149 §2.2 — the invoice list, so the export can print invoice NUMBERS. */
  inv,
  /* Round 13 (C69/C76) — subtask done-tick funnels into the canonical
     completion prompt (Log time / just finish / Cancel-revert). */
  onSubtaskDone,
  /* Round 14 (C85) — guarded case navigation for row tap-throughs: checks
     the viewer's actual case/cc visibility, toasts instead of navigating to
     a blank CV. Falls back to plain nav when the host doesn't pass it. */
  tryNavCase,
}: any) => {
  const { t: tr, locale, tIn } = useTranslation() as { t: (...a: [key: string, vars?: Record<string, unknown>]) => string; locale: string; tIn: (...a: [lng: string, key: string, vars?: Record<string, unknown>]) => string }; // 209 §6 — `tIn`
  /* 183 D.3 — the e-mail drawer's data at this mount (see the mount below):
     the viewer as the seed party, their addresses for reply-all's exclusion,
     this month as the window. */
  const deskMe = (co || []).find((x: { id?: unknown }) => Number(x.id) === Number(currentUser));
  const deskParties = deskMe ? [{ id: deskMe.id, name: deskMe.name, email: deskMe.m365Email || deskMe.email }] : [];
  const deskSelfEmails = [deskMe?.email, deskMe?.m365Email].filter(Boolean);
  /* 198 §C — the firm settings object, typed once at the seam (`unknown` is
     what `periodBounds`' axis input and the two mounts below take; the props
     arrive as `any`). */
  const periodAxis: unknown = firmSettings;
  const deskMonth = periodBounds({ kind: "month" }, periodAxis) ?? [null, null];
  const _dnFmtShort = new Intl.DateTimeFormat(locale || "en-GB", {
    weekday: "short",
  });

  const [evP, setEvP] = useState({ open: true, my: true, lim: 10, all: false });
  // Phase 23 (D2-corr) — inline event-subtask expansion (Set of expanded event ids).
  const [evSubsOpen, setEvSubsOpen] = useState(() => new Set());
  const toggleEvSubs = (id: any) =>
    setEvSubsOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const [evMode, setEvMode] = useState("agenda");
  const [schedOv, setSchedOv] = useState<Record<string, any>>({});
  const [evWeek, setEvWeek] = useState("this");
  const [tkWin, setTkWin] = useState("all");
  const [tkP, setTkP] = useState({ open: true, my: true, lim: 10, all: false });
  const teP = dTeP,
    setTeP = setDTeP;
  const [tkStatusFilt, setTkStatusFilt] = useState("all");
  const [tkMatrix, setTkMatrix] = useState(false);
  const [tkPage, setTkPage] = useState(0);
  // Phase M+2b: financial column on dashboard time entries. Original
  // `view_own_time_financial` was effectively admin-only on a non-Partner
  // install. Mapping: viewer.isAdmin || can('caseView.terms') (no caseId
  // scope on dashboard rows that span cases). caseView.terms defaults false
  // for every preset → admin-only by default; admin can flip the role
  // default to expose this column to Partners/Associates.
  const _perms = usePermissions();
  const tkUnloaded = useListsNotLoaded(TK_LIST).length > 0; // 210 §1
  const agUnloaded = useListsNotLoaded(AG_LIST).length > 0; // 210 §1
  // P20f — hidden qualifies the admin short-circuit (see usePermissions.hidden).
  const _canSeeOwnTimeFinancial =
    !_perms.hidden("caseView.terms.view") &&
    (!!_perms.viewer?.isAdmin || _perms.can("caseView.terms.view"));
  const _permIsAdmin = !!_perms.viewer?.isAdmin;
  /* BRIEF 115 §1 — the task rows are `CaseTaskRow` now (B.3: one renderer),
     so the old _tkFullEdit/_tkDocsTouch/_tkFieldEdit predicates live INSIDE
     the component (subtaskFullEdit, docsCanTouch, inlineOk), fed per row by
     this case-scoped bit — computable per mount because each row is its own
     mount. Verified term-for-term in the PR's union table: the component's
     `permIsAdmin ∪ canEditOthersTasks ∪ assignee/delegatee terms` is exactly
     what _tkFullEdit spelled, and its docsCanTouch is _tkDocsTouch. */
  const _tkCanEditOthers = (t: any) =>
    _perms.can("caseView.tasks.edit", { caseId: t.caseId, ccId: t.ccId });
  /* C85 — guarded tap-through (see prop note above). */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const [dashTkExp, setDashTkExp] = useState(() => new Set());
  /* 110 §1h → 115 §1 — subtasks stay a HOST Set (the component's counter
     writes it through setTkExpanded, and open-all pours ids in directly);
     the docs/notes drawers moved into CaseTaskRow with the rest of the row,
     so they are row-local again and open-all reaches them the way CV/CCV's
     does — the {seq, open} broadcast the rows subscribe to. */
  const [dashTkCast, setDashTkCast] = useState({ seq: 0, open: false });
  /* 115 §2 — the events pane's cast, same shape: its cards are CaseEventRow
     now, so notes/docs are row-local and the Set that addressed them is
     gone. */
  const [dashEvCast, setDashEvCast] = useState({ seq: 0, open: false });
  const _workLocList = useTaxonomyList("work_location");
  const _itemLevelList = useTaxonomyList("item_level");
  const _ITEM_LEVEL = itemLevelMap(_itemLevelList);
  // Phase 8 B1+B2 — raw case_type entries for ctc() meta-color lookup on
  // the dashboard time-table case-type pill.
  const _caseTypeEntries = useTaxonomy("case_type").entries;
  // Brief 57 §1 — the RAW task_status rows, because `meta.terminal` is what
  // decides whether a status is open and the tuple shape useTaxonomyList
  // returns carries no meta at all.
  const _taskStatusEntries = useTaxonomy("task_status").entries;
  useEffect(() => {
    setTkPage(0);
  }, [tkP.my, tkStatusFilt, tkWin]);
  const [tkPurgeConfirm, setTkPurgeConfirm] = useState(false);
  const [tkPurgeUndo, setTkPurgeUndo] = useState<any>(null);
  const tkPurgeTimer = useRef<any>(null);
  const [teDelUndo, setTeDelUndo] = useState<any>(null);
  const teDelTimer = useRef<any>(null);
  const delTeEntry = (entry: any) => {
    s(
      SK.te,
      te.filter((x: any) => x.id !== entry.id),
      sTe,
    );
    if (teDelTimer.current) clearTimeout(teDelTimer.current);
    setTeDelUndo(entry);
    teDelTimer.current = setTimeout(() => setTeDelUndo(null), UNDO_WINDOW_MS); // 209 §25
  };
  const undoTeEntry = () => {
    if (!teDelUndo) return;
    clearTimeout(teDelTimer.current);
    s(SK.te, [...te, teDelUndo], sTe);
    setTeDelUndo(null);
  };
  const schedOvLoaded = useRef<any>(null);
  // Phase asset-reservation C3 — fetch self-schedule overrides from the
  // server. Same 15-day window as before, but now from the canonical
  // ScheduleOverride table. The browser-local lx:schedOverride keys are
  // abandoned at cutover (recon RQ5).
  useEffect(() => {
    if (!currentUser || schedOvLoaded.current === currentUser) return;
    schedOvLoaded.current = currentUser;
    (async () => {
      const d0 = new Date();
      d0.setDate(d0.getDate() - 1);
      const dEnd = new Date(d0);
      dEnd.setDate(d0.getDate() + 13);
      const from = d0.toISOString().slice(0, 10);
      const to = dEnd.toISOString().slice(0, 10);
      try {
        const rows = (await getScheduleOverrides(from, to)) as
          | { collaboratorId: number; date: string; slots: unknown }[]
          | null;
        const ov: Record<string, any> = {};
        for (const r of rows || []) {
          if (r.collaboratorId === currentUser) {
            ov[r.date] = r.slots;
          }
        }
        setSchedOv(ov);
      } catch {
        setSchedOv({});
      }
    })();
  }, [currentUser]);
  const todS = new Date().toISOString().slice(0, 10);
  const in7D = new Date();
  in7D.setDate(in7D.getDate() + 7);
  const in7S = in7D.toISOString().slice(0, 10);
  // Week bounds (Mon–Sun)
  const now = new Date();
  const dow = now.getDay();
  const monD = new Date(now);
  monD.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
  const monS = monD.toISOString().slice(0, 10);
  const sunD = new Date(monD);
  sunD.setDate(monD.getDate() + 6);
  const sunS = sunD.toISOString().slice(0, 10);
  // Counters (C60 — ownership + dates are viewer-relevant)
  // Brief 57 §1 — the five counters that stood here now live in
  // src/lib/taskCounters.ts, because the OPEN TASKS column on both case lists
  // has to answer with the same number and could not call anything. What stood
  // here counted a WHITELIST of three status codes; the two lists counted the
  // complement of a BLACKLIST of the other three, and the two agreed only for
  // as long as `task_status` held exactly six codes. It is admin-editable.
  const _vDue = (t: any) => viewerDueDate(t, currentUser);
  const {
    open: openTkN,
    dueThisWeek: tkWeekN,
    overdue: tkOvdN,
    inProgress: tkWkInP,
    completedThisWeek: tkWkDone,
  } = countTasks(tk, {
    // `currentUser || null` and not `?? null`: what stood here was
    // `currentUser ? tk.filter(mine) : tk`, so any falsy viewer counted the
    // whole firm. That is also what the all/My toggle passes for "all".
    viewerId: currentUser || null,
    statusEntries: _taskStatusEntries,
    todayISO: todS,
    weekStartISO: monS,
    weekEndISO: sunS,
  });
  const weekMin = te
    .filter(
      (e: any) =>
        e.date >= monS &&
        e.date <= sunS &&
        (!currentUser || e.coId === currentUser),
    )
    .reduce((s: any, e: any) => s + (e.min || 0), 0);
  const mon1S =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-01";
  const monLastD = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const monLastS = monLastD.toISOString().slice(0, 10);
  const monthMin = te
    .filter(
      (e: any) =>
        e.date >= mon1S &&
        e.date <= monLastS &&
        (!currentUser || e.coId === currentUser),
    )
    .reduce((s: any, e: any) => s + (e.min || 0), 0);
  const weekHrs = weekMin / 60;
  const hrsClr =
    dow <= 3
      ? "var(--tp)"
      : dow === 4
        ? weekHrs >= nwh * 0.5
          ? "var(--gn)"
          : "var(--am)"
        : weekHrs >= nwh * 0.75
          ? "var(--gn)"
          : "var(--rd)";
  // Events — week period bounds
  const lastMonD = new Date(monD);
  lastMonD.setDate(monD.getDate() - 7);
  const lastMonS = lastMonD.toISOString().slice(0, 10);
  const lastSunD = new Date(lastMonD);
  lastSunD.setDate(lastMonD.getDate() + 6);
  const lastSunS = lastSunD.toISOString().slice(0, 10);
  const nextMonD = new Date(monD);
  nextMonD.setDate(monD.getDate() + 7);
  const nextMonS = nextMonD.toISOString().slice(0, 10);
  const nextSunD = new Date(nextMonD);
  nextSunD.setDate(nextMonD.getDate() + 6);
  const nextSunS = nextSunD.toISOString().slice(0, 10);
  const evWkStart =
    evWeek === "last" ? lastMonS : evWeek === "next" ? nextMonS : monS;
  const evWkEnd =
    evWeek === "last" ? lastSunS : evWeek === "next" ? nextSunS : sunS;
  // 58 §4 — the Events panel obeys the same chips as the strip above it. One
  // screen may not show a type in one panel and hide it in another.
  const evTypeOk = (a: { type?: string | null }) => eventTypeVisible(agTypeFilt, a);
  const evSrcAll = (
    evWeek === "all"
      ? ag.filter((a: any) => a.date && evTypeOk(a))
      : ag.filter(
          (a: any) =>
            a.date &&
            evTypeOk(a) &&
            a.date.slice(0, 10) >= evWkStart &&
            a.date.slice(0, 10) <= evWkEnd,
        )
  ).sort((a: any, b: any) => (a.date || "").localeCompare(b.date || ""));
  const evFilt =
    evP.my && currentUser
      ? evSrcAll.filter((a: any) =>
          (a.assignedCollaborators || []).includes(currentUser),
        )
      : evSrcAll;
  const evShown = evP.all ? evFilt : evFilt.slice(0, evP.lim);
  // Tasks — all statuses, open first then completed, sorted by the
  // viewer-relevant due date within each group (C60: a delegatee sorts a
  // mine-via-subtask task at HIS subtask's date).
  const tkSrc = [...tk].sort((a, b) => {
    const ac = a.status === "completed" ? 1 : 0,
      bc = b.status === "completed" ? 1 : 0;
    if (ac !== bc) return ac - bc;
    return (_vDue(a) || "\uFFFF").localeCompare(_vDue(b) || "\uFFFF");
  });
  const tkFiltBase =
    tkP.my && currentUser
      ? tkSrc.filter((t) => taskIsMine(t, currentUser))
      : tkSrc;
  const tkWkStart =
    tkWin === "last" ? lastMonS : tkWin === "next" ? nextMonS : monS;
  const tkWkEnd =
    tkWin === "last" ? lastSunS : tkWin === "next" ? nextSunS : sunS;
  const tkFilt = tkFiltBase
    .filter((t) =>
      tkStatusFilt === "finished"
        ? t.status === "completed" ||
          t.status === "cancelled" ||
          t.status === "archived"
        : tkStatusFilt === "progress"
          ? t.status === "inprogress"
          : tkStatusFilt === "waiting"
            ? t.status === "waiting"
            : t.status !== "completed" &&
              t.status !== "cancelled" &&
              t.status !== "archived",
    )
    .filter((t) => {
      if (tkWin === "all") return true;
      const d = _vDue(t);
      return d && d.slice(0, 10) >= tkWkStart && d.slice(0, 10) <= tkWkEnd;
    });
  /* 188 D.3 — the arithmetic is `pageOf`'s (ten; `Math.max(1, ceil)` ·
     `Math.min(page, tot - 1)` · the slice — this pane's own three expressions,
     identity replayed over 4,800 cases, 0 differences: the floor and the clamp
     were already here, so D.4 has nothing to change). The control is Pager —
     188 (5): this pane carried a fully inline copy of PagerBar, the ninth
     pager, on its own `dash.pagination.*` keys, which no detector keyed on a
     class or a component name could see. Those three keys now have no reader;
     the words they held are `common.*`'s, compared in en, fr and nl. */
  const { cur: tkCurPage, tot: tkTotalPages, pageItems: tkShown } = pageOf(tkFilt, tkPage);
  /* 118 §5 — does this pane's task table show a level column? Over the rows it
     actually renders, like the CV/CCV pane's own answer. */
  const __dashShowLevel = tkShown.some((t: any) => !!t.priority);
  /* 216 addendum 4 §8 (4) [RULED 79] — ONE LIST, BY DUE DATE. The grouping by
     matter that stood here (182: `tkGroups` keyed "ca:<id>" / "cc:<id>" / 0,
     the chip header rows between the groups) is gone — Laurent, 14/10: « the
     tasks are just listed per due date in Dashboard (not anymore by case like
     now) ». The order is `tkSrc`'s own: open before finished, then by the
     viewer-relative due date, an undated task last [ADVISOR]; each row names
     its matter on its second row (`caseBelow`, below). 182's rule survives in
     that name — a task names its matter of either kind. */
  /* 150 §1 — teSrc/teFiltUser/teFiltPeriod/teFilt/tePaged and the drawer-fees
     POST retired with the hand-rolled card: the block's own chain narrows and
     its table posts its own rows. useRoleLabels stays — the block takes
     _roleRows for its export and its fee request. */
  const { rows: _roleRows } = useRoleLabels();
  // 150 §4 — InvoiceView's canSeeCards generalised to the spans-matters
  // surface: admin always, else RP of at least one matter (the unscoped arm
  // of the original gate). A plain collaborator sees no counter row at all —
  // absent, never zero.
  const canSeeTeCounters =
    _permIsAdmin ||
    (ca || []).some((c: any) => (c.responsiblePartners || []).includes(currentUser)) ||
    (cc || []).some((c: any) => (c.responsiblePartners || []).includes(currentUser));
  const PH = (
    title: any,
    count: any,
    p: any,
    sp: any,
    myLbl: any,
    allLbl: any,
    onNew: any,
    newLbl: any,
    extra: any,
    hideToggle: any,
  ) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: p.open ? 10 : 2,
        flexWrap: "wrap",
      }}
    >
      <h4
        style={{ margin: 0, cursor: "pointer" }}
        onClick={() => sp((x: any) => ({ ...x, open: !x.open }))}
      >
        {title}
      </h4>
      {count > 0 && <span className="badge">{count}</span>}
      {currentUser && !hideToggle && (
        <button
          className="btn"
          style={{
            padding: "2px 8px",
            fontSize: FS.s10,
            borderRadius: RAD.r20,
            background: p.my ? "var(--as)" : "var(--bc)",
            color: p.my ? "var(--ac)" : "var(--tm)",
            border: "none",
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            keepScroll(() => sp((x: any) => ({ ...x, my: !x.my, all: false })));
          }}
        >
          {p.my ? `● ${myLbl}` : `○ ${allLbl}`}
        </button>
      )}
      {extra}
      {
        <button
          className="btn btn-primary"
          style={{ padding: "2px 7px", fontSize: FS.s10, marginLeft: "auto" }}
          onClick={onNew}
        >
          + {newLbl}
        </button>
      }
      <button
        style={{
          background: "none",
          border: "none",
          color: "var(--tm)",
          cursor: "pointer",
          fontSize: FS.s11,
          padding: "0 4px",
          marginLeft: 4,
        }}
        onClick={() => sp((x: any) => ({ ...x, open: !x.open }))}
      >
        {p.open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
    </div>
  );
  const More = (total: any, p: any, sp: any) =>
    !p.all && total > p.lim ? (
      <div
        style={{
          display: "flex",
          gap: 12,
          marginTop: 8,
          paddingTop: 6,
          borderTop: "1px solid rgba(255,255,255,.04)",
          fontSize: FS.s10_5,
        }}
      >
        <span
          className="link"
          style={{ cursor: "pointer" }}
          onClick={() => sp((x: any) => ({ ...x, lim: x.lim + 10 }))}
        >
          {tr("dash.show_n_more", { n: Math.min(total - p.lim, 10) })}
        </span>
        <span
          className="link"
          style={{ cursor: "pointer" }}
          onClick={() => sp((x: any) => ({ ...x, all: true }))}
        >
          {tr("dash.show_all", { n: total })}
        </span>
      </div>
    ) : null;
  return (
    <>
      <ListNotLoaded lists={KPI_LISTS}>
      <div
        style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}
      >
        <CC val={openTkN} label={tr("dash.kpi.open_tasks")} color="var(--tm)" />
        <CC
          val={tkOvdN}
          label={tr("dash.kpi.overdue_tasks")}
          color={tkOvdN > 0 ? "var(--rd)" : "var(--gn)"}
        />
        <CC val={tkWeekN} label={tr("dash.kpi.tasks_due_week")} />
        <CC
          val={tkWkInP}
          label={tr("dash.kpi.tasks_in_progress")}
          color="var(--am)"
        />
        <CC
          val={tkWkDone}
          label={tr("dash.kpi.tasks_completed_week")}
          color="var(--gn)"
        />
        <CC label={tr("dash.kpi.hours_target")}>
          <div
            style={{
              fontSize: FS.s18,
              fontWeight: 700,
              fontFamily: "'JetBrains Mono',monospace",
              lineHeight: 1,
              marginBottom: 4,
            }}
          >
            <span
              style={{ color: hrsClr }}
            >{`${Math.floor(weekMin / 60)}:${String(weekMin % 60).padStart(2, "0")}`}</span>
            <span style={{ fontSize: FS.s11, color: "var(--tm)", fontWeight: 400 }}>
              {" "}
              /{" "}
            </span>
            <span style={{ color: "var(--tp)", fontSize: FS.s18 }}>{nwh}</span>
            <span style={{ fontSize: FS.s11, color: "var(--tm)", fontWeight: 400 }}>
              {tr("dash.unit.hours")}
            </span>
          </div>
        </CC>
      </div>
      </ListNotLoaded>
      {(() => {
        const days = dayStrip(now);
        const showSched = evMode === "sched";
        const meWs =
          showSched && currentUser
            ? normalizeWeeklySchedule(
                co.find((c: any) => c.id === currentUser)?.weeklySchedule,
              )
            : null;
        const schedBg = (dw: any, dateIso: any) => {
          if (!meWs) return null;
          const dayIdx = schedDayIndex(dw);
          // 58 §5a — the axis is seven now, so this guard no longer hides
          // Saturday and Sunday. It was correct against a five-entry array.
          if (dayIdx < 0 || dayIdx >= SCHED_DAYS.length) return null;
          const slots = schedOv[dateIso] || meWs[dayIdx]?.slots || [];
          const blocks = [];
          let i = 0;
          while (i < slots.length) {
            if (slots[i]) {
              const loc = slots[i];
              let j = i;
              while (j < slots.length && slots[j] === loc) j++;
              blocks.push({ loc, len: j - i });
              i = j;
            } else {
              let j = i;
              while (j < slots.length && !slots[j]) j++;
              blocks.push({ loc: null, len: j - i });
              i = j;
            }
          }
          return blocks;
        };
        return (
          <div className="detail-card" style={{ marginBottom: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                margin: "0 0 8px",
              }}
            >
              <h4
                style={{
                  margin: 0,
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                  fontWeight: 600,
                }}
              >
                {tr("cc.14day_view")}
              </h4>
              <button
                className="btn"
                style={{
                  padding: "2px 8px",
                  fontSize: FS.s10,
                  borderRadius: RAD.r20,
                  background: showSched ? "var(--as)" : "var(--bc)",
                  color: showSched ? "var(--ac)" : "var(--tm)",
                  border: "none",
                }}
                onClick={() =>
                  setEvMode((v) => (v === "sched" ? "agenda" : "sched"))
                }
              >
                {showSched
                  ? `● ${tr("dash.my_schedule")}`
                  : `○ ${tr("dash.my_schedule")}`}
              </button>
            </div>
            {/* 58 §4 — the fourth surface. Same component, same persisted
                filter, same chip set: adding an event type in Settings puts a
                chip here without anybody creating an event. */}
            <EventTypeChips
              evtTypes={evtTypes}
              events={ag}
              value={agTypeFilt}
              onChange={setAgTypeFilt}
              t={tr}
              style={{ marginBottom: 6 }}
            />
            {/* 133 §3 — the twin of `matterListCfg.tsx`'s scroller: the same
                `HScroll`, so both strips get the modals' own hints and neither
                can drift from the other. 1172 inside a narrower dashboard
                column is exactly the clipping Laurent photographed. */}
            <HScroll>
            {/* 132 §3 — 14 full columns of 80 + 13 gaps of 4. 147 §1.3 — and
                now the number IS that sentence rather than a copy of it. */}
            <div style={{ display: "flex", gap: STRIP14_GAP, minWidth: STRIP14_MIN }}>
              {days.map((d) => {
                const ds = d.toISOString().slice(0, 10);
                const dw = d.getDay();
                const _ax = axisDayOf(schedAxis, dw);
                const isWknd = !_ax.working;
                /* 132 §6 — the twin of matterListCfg.tsx's strip: a legal
                   holiday of an observed country draws as a non-working day
                   does — the same background (133 §2), the muted label, the
                   name on HOVER. */
                const _hol = holidays ? holidays.on(ds) : [];
                const isHol = _hol.length > 0;
                const _holName = _hol.map((h: { name: string }) => h.name).join(" · ");
                const isOff = isWknd || isHol;
                /* 133 §4 — the shading is a firm setting; see the twin at
                   `matterListCfg.tsx`. The switch governs the BACKGROUND
                   only — `isOff` still decides the label colour, the hover
                   title and whether the schedule blocks are painted, so
                   turning it off returns this strip to 132 §3(b) rather than
                   making a weekend indistinguishable. */
                const shadeOff = isOff && calDisp?.stripNonWorking !== false;
                /* 132 §3 — `_ax.width` is no longer read; see the twin of
                   this strip at matterListCfg.tsx:659 for the reasoning and
                   for what happens to a firm that saved custom widths. THE
                   WIDTH RULING STANDS: 133 §2 changes only the background.
                   `working` still decides the label colour, and it still
                   decides whether the collaborator's own schedule blocks are
                   painted below.
                   132 §3(b) — "a non-working day carries no background" — is
                   WITHDRAWN by 133 §2, here and in the twin. The sentence that
                   stood in this comment claiming it "holds" is withdrawn with
                   it: a non-working day now takes `--nwd`, the field the
                   agenda already gives it. */
                const isToday2 = ds === todS;
                const dayEvs = ag.filter(
                  (a: any) =>
                    a.date &&
                    a.date.slice(0, 10) === ds &&
                    (showDL || a.type !== "DL") &&
                    // 58 §4 — the chips above this strip, one shared predicate
                    eventTypeVisible(agTypeFilt, a) &&
                    (!currentUser ||
                      !(a.assignedCollaborators || []).length ||
                      (a.assignedCollaborators || []).includes(currentUser)),
                );
                const shown4 = dayEvs.slice(0, 4);
                const more4 = dayEvs.length - 4;
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                /* 132 §6 — `!isWknd` became `!isOff`, and 133 §2 does not
                   change it. The reason has simply become plainer: a
                   collaborator's schedule blocks are a background, and a day
                   the firm does not work now carries a background of its OWN.
                   Painting both would put a schedule across a closed day and
                   overpaint the field that says it is closed. A firm does not
                   work on 21 July. */
                const sBlocks = !isOff ? schedBg(dw, ds) : null;
                return (
                  <div
                    key={ds}
                    style={{
                      flex: 2,
                      minWidth: STRIP14_COL,
                      flexShrink: 0,
                      /* 133 §2 — the twin of `matterListCfg.tsx`'s rule, and
                         the same order: TODAY outranks the field, then the
                         non-working field, then the ordinary wash. */
                      background: isToday2
                        ? "var(--as)"
                        : shadeOff
                          ? "var(--nwd)"
                          : "rgba(255,255,255,.02)",
                      /* 148 §3.1 — one of THREE sites carrying this exact
                         today-border (here, `matterListCfg.tsx` and
                         `AgendaView.tsx`; the brief named two). Measured off
                         the screenshot on this strip too and it reads the same
                         as its twin: all four edges painted, 103.4 vs the
                         neighbour's 19.3 on dark. The full refutation of the
                         half-intensity hypothesis is written once, at
                         `matterListCfg.tsx`. */
                      border: `1px solid ${isToday2 ? "color-mix(in srgb, var(--ac) 50%, transparent)" : "var(--br)"}`,
                      borderRadius: RAD.r8,
                      padding: "6px 7px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      minHeight: 110,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {sBlocks && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: RAD.r8,
                          overflow: "hidden",
                          pointerEvents: "none",
                          zIndex: Z.under,
                        }}
                      >
                        {sBlocks.map((b, bi) => {
                          const total =
                            sBlocks.reduce((s, x) => s + x.len, 0) || 1;
                          return (
                            <div
                              key={bi}
                              style={{
                                flex: b.len,
                                background: b.loc
                                  ? getWorkLocationMeta(b.loc, _workLocList)
                                      .color
                                  : "transparent",
                                opacity: 0.15,
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    <div
                      style={{
                        position: "relative",
                        zIndex: Z.stickyHead,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9.5,
                          fontWeight: 600,
                          color: isToday2
                            ? "var(--ac)"
                            : isOff
                              ? "var(--tm)"
                              : "var(--ts)",
                          marginBottom: 2,
                          textAlign: "center",
                          cursor: "pointer",
                          lineHeight: 1.3,
                        }}
                        /* 132 §6 — the name on hover, never printed: an 80px
                           cell cannot hold "Onze-Lieve-Vrouw-Hemelvaart". */
                        title={isHol ? _holName : undefined}
                        onClick={() => nav("ag")}
                      >
                        <span style={{ display: "block" }}>
                          {_dnFmtShort.format(d)}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontWeight: 400,
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: 8.5,
                          }}
                        >
                          {dd}/{mm}
                        </span>
                      </div>
                      {shown4.map((ev: any) => {
                        /* 191 §C — the matter through the one resolver: a
                           collective case's event takes ITS first word. */
                        const cs2 = resolveMatter({ caseId: ev.caId, ccId: ev.ccId }, ca, cc);
                        const word = cs2
                          ? (cs2.title || "").split(/\s+/)[0]
                          : (ev.title || "").split(/\s+/)[0];
                        const t = evtTypes.find((x: any) => x.code === ev.type) || {
                          code: ev.type || "?",
                          color: CT.c_8892a4,
                        };
                        const rv = parseInt(t.color.slice(1, 3), 16) || 136;
                        const gv = parseInt(t.color.slice(3, 5), 16) || 146;
                        const bv = parseInt(t.color.slice(5, 7), 16) || 164;
                        return (
                          <div
                            key={ev.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              cursor: "pointer",
                              overflow: "hidden",
                              minHeight: 16,
                            }}
                            onClick={() => {
                              if (ev.caId) {
                                /* C85 — guarded jump. */
                                if (_navCase("ca", ev.caId))
                                  queueScrollToEv(ev.id);
                              } else if (ev.ccId) {
                                /* 191 fold-in 5 — a collective case's event
                                   opens ITS collective case, the arm the
                                   matrix below already uses (the same
                                   guarded jump), not the event window. */
                                if (_navCase("cc", ev.ccId))
                                  queueScrollToEv(ev.id);
                              } else om("ag", ev);
                            }}
                            title={
                              cs2 ? cs2.title + " — " + cs2.ref : ev.title || ""
                            }
                          >
                            <span
                              style={{
                                flexShrink: 0,
                                fontSize: FS.s8_5,
                                padding: "1px 4px",
                                borderRadius: RAD.r3,
                                background: `rgba(${rv},${gv},${bv},.18)`,
                                color: t.color,
                                fontWeight: 600,
                                fontFamily: "'JetBrains Mono',monospace",
                                whiteSpace: "nowrap",
                                maxWidth: "none",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {t.code}
                            </span>
                            {/* 132 §3 — `!isWknd &&` stood here; a full-width
                                cell holds the word on every day. */}
                            <span
                              style={{
                                fontSize: FS.s9_5,
                                color: "var(--ts)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 24,
                              }}
                            >
                              {word}
                            </span>
                          </div>
                        );
                      })}{" "}
                      {more4 > 0 && (
                        <div
                          style={{
                            fontSize: FS.s8_5,
                            color: "var(--tm)",
                            textAlign: "center",
                            marginTop: "auto",
                            paddingTop: 2,
                          }}
                        >
                          {tr("dash.more_count", { count: more4 })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </HScroll>
          </div>
        );
      })()}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 10 }}
      >
        <div className="detail-card dashItemsPane" style={{ flex: "1 1 400px", minWidth: "min(300px, 100%)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: evP.open ? 10 : 2,
              flexWrap: "wrap",
            }}
          >
            <h4
              style={{ margin: 0, cursor: "pointer" }}
              onClick={() => setEvP((x) => ({ ...x, open: !x.open }))}
            >
              {tr("agenda.col.events")}
            </h4>
            {evFilt.length > 0 && <span className="badge">{evFilt.length}</span>}
            {currentUser && (
              <MyAllToggle {...({} as any)}
                my={evP.my}
                onMy={() =>
                  keepScroll(() =>
                    setEvP((x) => ({ ...x, my: true, all: false })),
                  )
                }
                onAll={() =>
                  keepScroll(() =>
                    setEvP((x) => ({ ...x, my: false, all: false })),
                  )
                }
                counts={{
                  my: evSrcAll.filter((a: any) =>
                    (a.assignedCollaborators || []).includes(currentUser),
                  ).length,
                  all: evSrcAll.length,
                }}
              />
            )}
            <SegmentedControl {...({} as any)}
              value={evWeek}
              options={[
                ["all", tr("common.all")],
                ["last", tr("agenda.range.last")],
                ["this", tr("agenda.range.this_week")],
                ["next", tr("agenda.range.next")],
              ]}
              onChange={(k: any) => keepScroll(() => setEvWeek(k))}
              counts={{ all: ag.filter((a: any) => a.date).length }}
            />
            {/* 110 §1h — open/close every card's drawers.
                115 §2 — the CV/CCV mechanism now that the cards are
                CaseEventRow: subtasks are host state, set directly (111 §2 —
                only cards that HAVE some); docs/notes are row-local and ride
                the {seq, open} tick, each card opening only a drawer with
                content (CaseEventRow's own 111 §2 effect). */}
            <button
              type="button"
              title={
                dashEvCast.open
                  ? tr("drawers.close_all", { fallback: "Close all drawers" })
                  : tr("drawers.open_all", { fallback: "Open all drawers" })
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                display: "inline-flex",
                alignItems: "center",
                color: dashEvCast.open ? "var(--ac)" : "var(--tp)",
                opacity: dashEvCast.open ? 1 : 0.6,
              }}
              onClick={() => {
                const open = !dashEvCast.open;
                setDashEvCast((p) => ({ seq: p.seq + 1, open }));
                setEvSubsOpen(
                  new Set(
                    open
                      ? evFilt
                          .filter(
                            (e: { subtasks?: unknown }) =>
                              (Array.isArray(e.subtasks) ? e.subtasks : [])
                                .filter(
                                  (s: { archived?: boolean } | null) =>
                                    s && !s.archived,
                                ).length > 0,
                          )
                          .map((e: { id: number }) => e.id)
                      : [],
                  ),
                );
              }}
            >
              {dashEvCast.open ? (
                <ChevronsDownUp size={14} />
              ) : (
                <ChevronsUpDown size={14} />
              )}
            </button>
            {/* 111 §1a — the event AddButton is gone: creation happens in
                CV/CCV only (ruled). The chevron inherits its marginLeft:auto. */}
            <button
              style={{
                background: "none",
                border: "none",
                color: "var(--tm)",
                cursor: "pointer",
                fontSize: FS.s11,
                padding: "0 4px",
                marginLeft: "auto",
              }}
              onClick={() => setEvP((x) => ({ ...x, open: !x.open }))}
            >
              {evP.open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          </div>
          {evP.open && agUnloaded && <ListNotLoaded lists={AG_LIST} />}
          {evP.open && !agUnloaded && (
            <>
              {evShown.length === 0 && (
                <p
                  style={{ fontSize: FS.s11, color: "var(--tm)", margin: "4px 0" }}
                >
                  {(() => {
                    const _isMine = evP.my && currentUser;
                    const _key =
                      "dash.empty.events_" + evWeek + (_isMine ? "_mine" : "");
                    return tr(_key);
                  })()}
                </p>
              )}
              {/* BRIEF 115 §2 — THE CARDS ARE `CaseEventRow`, THE FULL CV/CCV
                  VARIANT (B.3), same union discipline as §1: the whole-card
                  navigate-and-flash click (onRowNav), the 110 §1b view-only
                  regime (drawersReadOnly), the open-all broadcast
                  (drawersCast) — plus the MATTER CHIP, this pane's own
                  contribution (matterChip: a CV/CCV card sits inside its
                  matter and never needed one). agLocked/canEditAgenda are
                  locked/false by construction: this pane has no lock
                  affordance and has never offered inline editing, the pencil
                  or the archive button — the card's click is the edit path.
                  The stage pill's feeds (stages/caseStageEntries) are NOT
                  threaded, deliberately: scrollToStage targets the CV/CCV
                  timeline DOM, which this view does not render — the same
                  reason the compact column passes neither (109 §7f). */}
              {evShown.map((ev: any) => {
                const _cs = ev.caId ? ca.find((c: any) => c.id === ev.caId) : null;
                const _cc = ev.ccId
                  ? (cc || []).find((c: any) => c.id === ev.ccId)
                  : null;
                const _linked = _cs || _cc;
                return (
                  <CaseEventRow
                    key={ev.id}
                    ev={ev}
                    co={co}
                    currentUser={currentUser}
                    todayStr={todS}
                    priMap={_ITEM_LEVEL}
                    itemLevelList={_itemLevelList}
                    evtTypes={evtTypes}
                    etTag={etTag}
                    evtLongestCode={evtLongestCode}
                    coAvs={coAvs}
                    locIcon={locIcon}
                    ag={ag}
                    sAg={sAg}
                    s={s}
                    SK={SK}
                    evExpanded={evSubsOpen}
                    toggleEvExpanded={toggleEvSubs}
                    agLocked={true}
                    canEditAgenda={false}
                    canEditOthersTasks={false}
                    activeTimers={activeTimers}
                    startTimer={startTimer}
                    openModal={om}
                    /* locked → the archive affordance never renders; the
                       handler gets the compact column's noop. */
                    onDeleteEvent={() => {}}
                    drawersCast={dashEvCast}
                    matterRow={_cc || _cs}
                    drawersReadOnly
                    /* 216 addendum 7 §11 (2) [RULED 84] — the counter centred in its own column, the pill beside
                       it, the drawers inside the card (CaseEventRow) */
                    dashboardCard
                    onRowNav={
                      _linked
                        ? () => {
                            queueScrollToEv(ev.id);
                            if (_cc) nav("cc", _cc.id);
                            else nav("ca", _cs.id);
                          }
                        : ev.caId != null || ev.ccId != null
                          ? () => {
                              /* Phase 30 Stage-3 (C-1) — linked but not in
                                 the viewer's scoped lists (firm-wide REU /
                                 delegation orphan): the C85 guard toasts
                                 instead of the old silent no-op. Same
                                 pattern as the task rows. */
                              if (
                                _navCase(
                                  ev.caId != null ? "ca" : "cc",
                                  ev.caId != null ? ev.caId : ev.ccId,
                                )
                              )
                                queueScrollToEv(ev.id);
                            }
                          : undefined
                    }
                    /* 216 addendum 4 §8 (4) [RULED 79] — the case name at the
                       right end of the second row (`atRight`), for a collective
                       case's event too; a click opens the matter at the event,
                       as the chip did. */
                    matterChip={
                      _linked
                        ? {
                            label: _linked.title,
                            title: _linked.title,
                            atRight: true,
                            onClick: () => {
                              queueScrollToEv(ev.id);
                              if (_cc) nav("cc", _cc.id);
                              else nav("ca", _cs.id);
                            },
                          }
                        : undefined
                    }
                  />
                );
              })}
              {More(evFilt.length, evP, setEvP)}
            </>
          )}
          {/* end agenda mode */}
        </div>
        <div className="detail-card dashItemsPane" style={{ flex: "1 1 400px", minWidth: "min(300px, 100%)" }}>
          {(() => {
            const finTasks = tk.filter(
              (t: any) => t.status === "completed" || t.status === "archived",
            );
            const finCount = finTasks.length;
            const ownFin = finTasks.filter(
              (t: any) => !t.createdBy || t.createdBy === currentUser,
            );
            const otherFin = finTasks.filter(
              (t: any) =>
                t.createdBy &&
                t.createdBy !== currentUser &&
                t.status !== "archived",
            );
            const doPurge = () => {
              if (!ownFin.length && !otherFin.length) return;
              const ownIds = new Set(ownFin.map((t: any) => t.id));
              const otherIds = new Set(otherFin.map((t: any) => t.id));
              /* 112 §6 — BOTH arms leave the array, so the engine fires the
                 REAL archive (SK.tk d: = archiveTask) for both. The other-
                 people arm used to write `status: "archived"` in place — the
                 string mechanism whose rows stayed on every live surface and
                 never reached ArchiveView (the glance's sighting). Now every
                 archived row's home is ArchiveView, both arms. */
              s(
                SK.tk,
                tk.filter(
                  (t: any) => !ownIds.has(t.id) && !otherIds.has(t.id),
                ),
                sTk,
              );
              setTkPurgeUndo({
                tasks: ownFin,
                archived: otherFin,
                count: ownFin.length + otherFin.length,
              });
              setTkPurgeConfirm(false);
              if (tkPurgeTimer.current) clearTimeout(tkPurgeTimer.current);
              tkPurgeTimer.current = setTimeout(
                () => setTkPurgeUndo(null),
                UNDO_WINDOW_MS, // 209 §25
              );
            };
            const undoPurge = () => {
              if (!tkPurgeUndo) return;
              clearTimeout(tkPurgeTimer.current);
              /* 112 §6 — both arms were engine-archived, so both restore the
                 same way the own arm always did: re-created from snapshot.
                 The other-people rows keep the old undo's status mapping
                 (they come back 'completed', as before). */
              s(
                SK.tk,
                [
                  ...tk,
                  ...tkPurgeUndo.tasks,
                  ...(tkPurgeUndo.archived || []).map((t: any) => ({
                    ...t,
                    status: "completed",
                  })),
                ],
                sTk,
              );
              setTkPurgeUndo(null);
            };
            return (
              <>
                {tkPurgeConfirm && (
                  <ModalLayout
                    title={tr("dash.cleanup.title")}
                    showX={false}
                    rawBody
                    footer={null}
                    maxWidth={380}
                  >
                      <div style={{ padding: "16px 20px" }}>
                        {ownFin.length > 0 && (
                          <p style={{ fontSize: FS.s12, marginBottom: 4 }}>
                            {tr("dash.cleanup.delete_own", {
                              count: ownFin.length,
                            })}
                          </p>
                        )}
                        {otherFin.length > 0 && (
                          <p style={{ fontSize: FS.s12, marginBottom: 4 }}>
                            {tr("dash.cleanup.archive_assigned", {
                              count: otherFin.length,
                            })}
                          </p>
                        )}
                        <p
                          style={{
                            fontSize: FS.s11,
                            color: "var(--tm)",
                            marginBottom: 20,
                          }}
                        >
                          {tr("dash.cleanup.no_undo_5s")}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "6px 14px" }}
                            onClick={() => setTkPurgeConfirm(false)}
                          >
                            {tr("common.cancel")}
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{
                              padding: "6px 14px",
                              color: "var(--rd)",
                              borderColor: "var(--rd)",
                            }}
                            onClick={doPurge}
                          >
                            {tr("dash.cleanup.do", {
                              count: ownFin.length + otherFin.length,
                            })}
                          </button>
                        </div>
                      </div>
                  </ModalLayout>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: tkP.open ? 10 : 2,
                    flexWrap: "wrap",
                  }}
                >
                  <h4
                    style={{ margin: 0, marginRight: 12, cursor: "pointer" }}
                    onClick={() => setTkP((x) => ({ ...x, open: !x.open }))}
                  >
                    {tr("case.tasks")}
                  </h4>
                  {tkFilt.length > 0 && (
                    <span className="badge">{tkFilt.length}</span>
                  )}
                  {currentUser && (
                    <MyAllToggle {...({} as any)}
                      my={tkP.my}
                      onMy={() => {
                        keepScroll(() =>
                          setTkP((x) => ({ ...x, my: true, all: false })),
                        );
                        setTkPage(0);
                      }}
                      onAll={() => {
                        keepScroll(() =>
                          setTkP((x) => ({ ...x, my: false, all: false })),
                        );
                        setTkPage(0);
                      }}
                      counts={{
                        my: tkSrc.filter((t) => taskIsMine(t, currentUser))
                          .length,
                        all: tkSrc.length,
                      }}
                    />
                  )}
                  <SegmentedControl {...({} as any)}
                    value={tkStatusFilt}
                    options={[
                      ["all", tr("common.all")],
                      ["progress", tr("case.tk.progress")],
                      ["waiting", tr("case.tk.waiting")],
                      ["finished", tr("case.tk.finished")],
                    ]}
                    onChange={(k: any) => {
                      setTkStatusFilt(k);
                      setTkPage(0);
                    }}
                    counts={{
                      all: tkFiltBase.filter(
                        (t) =>
                          t.status !== "completed" &&
                          t.status !== "cancelled" &&
                          t.status !== "archived",
                      ).length,
                      progress: tkFiltBase.filter(
                        (t) => t.status === "inprogress",
                      ).length,
                      waiting: tkFiltBase.filter((t) => t.status === "waiting")
                        .length,
                      finished: tkFiltBase.filter(
                        (t) =>
                          t.status === "completed" ||
                          t.status === "cancelled" ||
                          t.status === "archived",
                      ).length,
                    }}
                  />
                  <SegmentedControl {...({} as any)}
                    value={tkWin}
                    options={[
                      ["all", tr("common.all")],
                      ["last", tr("agenda.range.last")],
                      ["this", tr("agenda.range.this_week")],
                      ["next", tr("agenda.range.next")],
                    ]}
                    onChange={(k: any) => {
                      setTkWin(k);
                      setTkPage(0);
                    }}
                    counts={{ all: tkFiltBase.filter((t) => _vDue(t)).length }}
                  />
                  {/* 190 §E.1 — the dashboard matrix carries its own key
                      (`surface.eisenhower.dashboard`). */}
                  {_perms.can("surface.eisenhower.dashboard") && (
                  <button
                    className="scIcHov"
                    /* 190 round 6 [AA] — glows while the dashboard matrix is
                       open (the lock's declarations, SharedUI OPEN_MODE_GLOW). */
                    style={{
                      background: tkMatrix ? OPEN_MODE_GLOW.background : "none",
                      border: tkMatrix ? OPEN_MODE_GLOW.border : "none",
                      animation: tkMatrix ? OPEN_MODE_GLOW.animation : undefined,
                      borderRadius: RAD.r6,
                      cursor: "pointer",
                      padding: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: tkMatrix ? OPEN_MODE_ICON : "var(--tp)",
                      opacity: tkMatrix ? 1 : 0.6,
                    }}
                    onClick={() => setTkMatrix((v) => !v)}
                    title={tr("case.matrix_tip")}
                  >
                    <Grid2x2 size={14} />
                  </button>
                  )}
                  {tkStatusFilt === "finished" &&
                    ownFin.length + otherFin.length > 0 && (
                      <button
                        className="btn"
                        style={{
                          padding: "2px 8px",
                          fontSize: FS.s10,
                          borderRadius: RAD.r20,
                          background: "rgba(248,113,113,.12)",
                          color: "var(--rd)",
                          border: "1px solid rgba(248,113,113,.35)",
                        }}
                        onClick={() => setTkPurgeConfirm(true)}
                      >
                        {tr("dash.cleanup.btn_finished")}
                      </button>
                    )}
                  {/* 110 §1h — open/close every row's three drawers.
                      115 §1 — the CV/CCV mechanism now that the rows are
                      CaseTaskRow: subtasks are host state and set directly
                      (111 §2 — only rows that HAVE some, the counter's own
                      non-archived fact); docs/notes are row-local and ride
                      the {seq, open} tick, each row opening only a drawer
                      with content (CaseTaskRow's own 111 §2 effect). */}
                  <button
                    type="button"
                    title={
                      dashTkCast.open
                        ? tr("drawers.close_all", {
                            fallback: "Close all drawers",
                          })
                        : tr("drawers.open_all", {
                            fallback: "Open all drawers",
                          })
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      color: dashTkCast.open ? "var(--ac)" : "var(--tp)",
                      opacity: dashTkCast.open ? 1 : 0.6,
                    }}
                    onClick={() => {
                      const open = !dashTkCast.open;
                      setDashTkCast((p) => ({ seq: p.seq + 1, open }));
                      type ArchivableRow = { archived?: boolean } | null;
                      const hasSubs = (x: { subtasks?: unknown }) =>
                        (Array.isArray(x.subtasks) ? x.subtasks : []).filter(
                          (s: ArchivableRow) => s && !s.archived,
                        ).length > 0;
                      setDashTkExp(
                        new Set(
                          open
                            ? tkFilt
                                .filter(hasSubs)
                                .map((x: { id: number }) => x.id)
                            : [],
                        ),
                      );
                    }}
                  >
                    {dashTkCast.open ? (
                      <ChevronsDownUp size={14} />
                    ) : (
                      <ChevronsUpDown size={14} />
                    )}
                  </button>
                  {/* 111 §1a — the task AddButton is gone: creation happens in
                      CV/CCV only (ruled). The chevron inherits its marginLeft:auto. */}
                  <button
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--tm)",
                      cursor: "pointer",
                      fontSize: FS.s11,
                      padding: "0 4px",
                      marginLeft: "auto",
                    }}
                    onClick={() => setTkP((x) => ({ ...x, open: !x.open }))}
                  >
                    {tkP.open ? (
                      <ChevronUp size={11} />
                    ) : (
                      <ChevronDown size={11} />
                    )}
                  </button>
                </div>
                {tkPurgeUndo && (
                  <div
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                      padding: "7px 10px 10px",
                      background: "var(--rd)",
                      borderRadius: RAD.r8,
                      fontSize: FS.s11,
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        color: CT.c_fff,
                        fontSize: FS.s10_5,
                        fontWeight: 500,
                      }}
                    >
                      {tr("dash.cleanup.toast", { count: tkPurgeUndo.count })}
                    </span>
                    <button
                      type="button"
                      style={{
                        padding: "2px 10px",
                        fontSize: FS.s10,
                        background: "none",
                        border: "1.5px solid rgba(255,255,255,.8)",
                        color: CT.c_fff,
                        borderRadius: RAD.r6,
                        cursor: "pointer",
                        fontWeight: 600,
                        flexShrink: 0,
                        fontFamily: "inherit",
                      }}
                      onClick={undoPurge}
                    >
                      {tr("common.undo")}
                    </button>
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        height: 3,
                        background: "rgba(255,255,255,.45)",
                        animation: `ud ${String(UNDO_WINDOW_MS)}ms linear forwards`,
                      }}
                    />
                  </div>
                )}
              </>
            );
          })()}
          {/* BRIEF 104 §1a — `priMap` no longer passed: the matrix drew a level
              letter and no longer does. */}
          {tkP.open && tkUnloaded && <ListNotLoaded lists={TK_LIST} />}
          {tkP.open && !tkUnloaded && tkMatrix && _perms.can("surface.eisenhower.dashboard") && (
            <TaskMatrix
              tasks={tkFilt}
              ca={ca}
              cc={cc} // 111 §5c — itemLinks feed for cc rows
              tkTypes={tkTypes}
              getQuadrant={(t: any) =>
                (userMatrix && userMatrix[t.id]) || eisenAuto(t.type)
              }
              onMove={(tkId: any, q: any) => {
                if (userMatrixSetQuadrant) userMatrixSetQuadrant(tkId, q);
              }}
              onTaskClick={(t: any) => {
                /* C85 — Eisenhower jump rides the same guard as title
                   clicks (cc context included). 110 §1b — measured: this was
                   the ONE Dashboard jump that did not flash its landing; the
                   title/event clicks already queue it. */
                if (t.caseId) {
                  if (_navCase("ca", t.caseId)) queueScrollToTk(t.id);
                } else if (t.ccId) {
                  if (_navCase("cc", t.ccId)) queueScrollToTk(t.id);
                }
              }}
              onClose={() => setTkMatrix(false)}
            />
          )}
          {tkP.open && !tkUnloaded && !(tkMatrix && _perms.can("surface.eisenhower.dashboard")) && (
            <>
              {tkShown.length === 0 && (
                <p
                  style={{ fontSize: FS.s11, color: "var(--tm)", margin: "4px 0" }}
                >
                  {tkP.my && currentUser
                    ? tr("dash.empty.no_tasks_mine")
                    : tr("dash.empty.no_tasks")}
                </p>
              )}
              {/* BRIEF 115 §1 — THE ROWS ARE `CaseTaskRow`, THE FULL CV/CCV
                  VARIANT (B.3: one renderer for one item). What this host
                  keeps is LIST structure — the no-case grouping and the
                  case-chip headers, rendered BETWEEN rows as header <tr>s in
                  ONE table, so every group shares one column grid. What moved
                  into the component rides its union props: the whole-row
                  navigate-and-flash click (onRowNav), the 110 §1b view-only
                  drawer regime (drawersReadOnly), the C60 viewer-relative due
                  date (viewerDue), the 110 §1h open-all broadcast
                  (drawersCast) and the per-task case-scoped grant
                  (canEditOthersTasks). tkLocked is TRUE by construction: this
                  pane has no lock affordance and has never offered inline
                  editing, the pencil or the archive button — the row's
                  navigation is the edit path (110 §1b). */}
              {/* 216 addendum 6 §10 (2) [RULED 81] — EACH TASK IS A CARD OF ITS OWN, like a matter's event card
                  (Laurent, 15/10: « put the events and tasks in an individual area with rounder corners,
                  contrasting with background like in CV/CCV Events »): the row's table stands inside an
                  `.event-item` — the card's background, border and corners; `.tkCard` releases the card's own flex
                  and padding so the row's cells set the air — ONE TABLE PER CARD, so the row, its case pill
                  (CaseTaskRow's second row, the event card's own chip) and its drawers (the subtasks, the
                  documents, the note — rows of the same table) are INSIDE the card, which grows to hold them;
                  `tkNoLines` on each (addendum 4: no hairline between a task and its subtasks). What each row
                  shows is what it showed (his « keep items as is »). MEASURED why the cards did not contrast: the
                  pane (`.detail-card`) and the card (`.event-item`) share `--bc`; the two panes take the page's
                  surface (`dashItemsPane`, App.css), as a matter's sections sit on it. */}
              {tkShown.length > 0 && (
                <div data-tk-cards="">
                      {/* BRIEF 118 §5 — the level column is the TABLE's
                          question, and this table's rows are `tkShown`. It has
                          no `<thead>`, so there is no sort control to remove
                          with it; MEASURED here before the change, it still
                          held 20px of nothing with no level on any row (28
                          with one). */}
                      {tkShown.map((t: any) => {
                        /* 182 / 166 §E — a task names its matter of either
                           kind: the case row, else the collective case row,
                           each read through a typed view. */
                        const row = t as { caseId?: number | null; ccId?: number | null; caseTitle?: string; ccTitle?: string; caseRef?: string; ccRef?: string };
                        const kind: "ca" | "cc" = row.caseId != null ? "ca" : "cc";
                        const rows = (kind === "cc" ? (cc ?? []) : ca) as { id: number; title?: string; ref?: string }[];
                        const cs2 = row.caseId != null || row.ccId != null
                          ? (rows.find((c) => c.id === (kind === "cc" ? row.ccId : row.caseId)) ?? null)
                          : null;
                        /* C65 — a delegation-induced assignee is NOT on the
                           case team, so the case is absent from his lists; the
                           task row itself carries the title (server-attached
                           caseTitle / ccTitle). Display-only: no nav. */
                        const fbTitle = cs2 ? null : (row.caseTitle ?? row.ccTitle ?? null);
                        const caseBelow = cs2
                          ? {
                              label: cs2.title ?? "",
                              title: [cs2.title, cs2.ref].filter(Boolean).join(" — "),
                              onClick: () => {
                                queueScrollToTk(t.id);
                                nav(kind, cs2.id);
                              },
                            }
                          : fbTitle
                            ? { label: fbTitle, title: [fbTitle, row.caseRef ?? row.ccRef].filter(Boolean).join(" — ") }
                            : undefined;
                        return (
                          <div key={t.id} className="event-item tkCard" data-tk-card={t.id}>
                            <Table className="tkNoLines">
                              <tbody>
                                <CaseTaskRow
                                  showLevel={__dashShowLevel}
                                  t={t}
                                  co={co}
                                  currentUser={currentUser}
                                  tk={tk}
                                  tkTypes={tkTypes}
                                  itemLevelList={_itemLevelList}
                                  priMap={_ITEM_LEVEL}
                                  activeTimers={activeTimers}
                                  tkLocked={true}
                                  canEditOthersTasks={_tkCanEditOthers(t)}
                                  permIsAdmin={_permIsAdmin}
                                  isRP={false}
                                  tkExpanded={dashTkExp}
                                  setTkExpanded={setDashTkExp}
                                  TkStatusTag={TkStatusTag}
                                  startTimer={startTimer}
                                  svTkData={svTkData}
                                  sTk={sTk}
                                  openModal={om}
                                  /* locked → the archive affordance never
                                     renders; the handler is part of the
                                     required list and gets the compact
                                     column's noop. */
                                  onDeleteTask={() => {}}
                                  onSubtaskDone={onSubtaskDone}
                                  drawersCast={dashTkCast}
                                  matterRow={cs2 ?? undefined}
                                  drawersReadOnly
                                  viewerDue={_vDue(t)}
                                  /* 216 addendum 4 §8 (4) — the case name on the row's second line */
                                  caseBelow={caseBelow}
                                  onRowNav={
                                    cs2
                                      ? () => {
                                          queueScrollToTk(t.id);
                                          nav(kind, cs2.id);
                                        }
                                      : t.caseId != null || t.ccId != null
                                        ? () => {
                                            /* C85 — the case exists but is
                                               not in the viewer's list
                                               (off-team delegated row) or
                                               rides a cc: the guard either
                                               navigates or toasts instead of
                                               a blank CV. */
                                            if (
                                              _navCase(
                                                t.caseId != null ? "ca" : "cc",
                                                t.caseId != null
                                                  ? t.caseId
                                                  : t.ccId,
                                              )
                                            )
                                              queueScrollToTk(t.id);
                                          }
                                        : undefined
                                  }
                                />
                              </tbody>
                            </Table>
                          </div>
                        );
                      })}
                </div>
              )}
              {tkTotalPages > 1 && <Pager cur={tkCurPage} tot={tkTotalPages} setPage={setTkPage} />}
            </>
          )}
        </div>
      </div>
      <PersonalMessagesCard
        currentUser={currentUser}
        co={co}
        ca={ca}
        cc={cc}
        chatMsgs={chatMsgs}
        gChat={gChat}
        chatRead={chatRead}
        nav={nav}
        navCase={_navCase}
        setChatOpen={setChatOpen}
      />

      {/* 174 §4 — THE EMAIL DESK. Laurent: "bill the emails without jumping and
          searching in each case." It sits ABOVE the all-matters time block
          because that is the order of the act: triage the mail, record it, then
          look at what was recorded. 183 D.3 — THE DESK IS THE DRAWER: the same
          EmailDeepSearchDrawer the two case views mount, mounted inline under
          this card's header with no matter (`urlBase="/mail"`, the matter-less
          deep read, 183 D.1); the record, save and paper-clip acts ask which
          matter first and post to the CHOSEN matter's own endpoints (183 D.2),
          so the gate follows the matter with no new permission surface here.
          WHAT IS DATA, NOT CODE, at this mount: the query is seeded with the
          viewer's own address (the desk's "no terms means my mail", now
          visible and editable — a $search needs terms), the window opens on
          THIS MONTH (the desk's daily-billing default, 174 §4), and the
          matter choice receives the lists the desk received. */}
      <div className="detail-card" style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <Mail size={13} style={{ color: "var(--tm)" }} />
          <span style={{ fontSize: FS.s12, fontWeight: 600 }}>
            {tr("dashEmails.title", { fallback: "Emails to bill" })}
          </span>
        </div>
        <Suspense fallback={null}>
          <EmailDeepSearchDrawer
            inline
            open
            onClose={NOOP}
            urlBase="/mail"
            parties={deskParties}
            firmSettings={periodAxis}
            defaultFrom={deskMonth[0] ?? ""}
            defaultTo={deskMonth[1] ?? ""}
            defaultPeriod="month" // 194 §C.1 — the row's menu names the month those dates are
            toast={toast}
            canBill
            canArchive
            /* 184 C.4 [RULED (iii)] — the footer works here too: a matter-less
               mount, so the drawer asks "Log time on which matter?" (C.3) and
               hands the answer back with the message; the time modal opens on
               the chosen matter, locked (`_caseFixed`, `_lockCo` — the
               prefill's own keys), the e-mail prefilled. A double log on a
               collective case is the `@@unique([ccId, emailGraphId,
               collaboratorId])` twin's friendly 409 (timeEntries.js). */
            canLogTime
            onLogTime={(msg, m) => {
              if (!m) return;
              // 209 §6 — an e-mail with no subject is named in the chosen MATTER's language (none: left to type)
              const matterRow = ((m.kind === "cc" ? cc : ca) as { id: number }[] | undefined)?.find((x) => x.id === m.id);
              const matterLang = storedCaseLanguage(matterRow);
              om("te", emailLogTimePrefill(msg as EmailForTimeEntry, m, currentUser,
                matterLang ? tIn(matterLang, "email.no_subject", { fallback: "(no subject)" }) : null));
            }}
            selfEmails={deskSelfEmails}
            matterChoice={{ cases: ca, collectiveCases: cc, getRecentCases, currentUser }}
          />
        </Suspense>
      </div>

      {/* 151C §2 — overflow VISIBLE on this one card: `.detail-card` is
          overflow-x auto, which makes it a scroll container and would trap
          the block's sticky counter bar at the card's own bottom edge
          (phase-45's overflow-traps-sticky). The block's scroll window owns
          any x-overflow of the table now. */}
      <div className="detail-card" style={{ overflow: "visible" }}>
        {/* ── 150 §1 SURFACE 4/4 — THE DASHBOARD MOUNTS THE BLOCK ─────────────
            The hand-rolled card this replaces was the block re-implemented by
            hand, one generation older: its own header row, its own
            export/import buttons and file input, its own ImportPreview, a
            billed-AXIS strip (all/unbilled/billed/invoiced — the pre-mark
            model), a 10-row pager, and a table whose money column was the
            drawer-fees POST. All of it retires into ONE CaseTimeEntriesSection
            mount; teBilFilt, tePage2, teImportFile/teFileRef and the
            drawer-fees machinery die with it.

            THE DIFFERENCES ARE DATA (the §1 rule), and each one is a prop:
            · kind="all" — the population spans matters; a row's matter is
              resolved per row (the table's invoice-kind discipline).
            · initialUserFilter=viewer — "My time" is the USER FILTER's
              default, not a hard filter; an RP or admin can now widen it,
              which the card structurally could not.
            · initialPeriod=work week — the card was hard-filtered to the week
              (its tePeriod toggle had NO writer, measured); the period filter
              opens there and can now leave.
            · showCaseFilter — this is the one surface whose population spans
              matters with no host scope control, so the case filter renders.
            · canTerms=own-time-financial right — the Amount column keeps the
              exact gate the card gave it.
            · canMarkBilled=admin — marking spans matters here, and a single
              boolean cannot carry per-matter RP-ness; a keyless viewer gets
              no strip and the list shows ALL (150 §2's rule). RPs mark where
              the hint is scoped: CV, CCV, Invoicing.
            · no onProForma / no bulk hints / no onDoubles — a pro forma
              binds ONE matter and a spans-matters selection cannot; the
              dedup review is a host modal this surface does not carry.
            · pagination is gone: the block's tab strip, filters and sort are
              the navigation; the pager was the card's own invention.
            The single-row delete keeps this host's own undo (delTeEntry /
            teDelUndo below), exactly as every host hands its own onDelete. */}
        <CaseTimeEntriesSection
          {...({} as any)}
          kind="all"
          cs={null}
          co={co}
          currentUser={currentUser}
          /* 198 §C — the SETTINGS OBJECT, not the resolved axis: the period
             filter's two accounting-year kinds read the firm's start off it
             (D.3); `periodBounds` resolves the work week from it just the same
             (InvoiceView already passed it this way). */
          schedAxis={periodAxis}
          te={te}
          sTe={sTe}
          s={s}
          SK={SK}
          ca={ca}
          cc={cc || []}
          inv={inv || []}
          canSeeCounters={canSeeTeCounters}
          /* 151D §2(e) — this surface's list sits on the card colour, so the
             counters pane wears it too (white in the light theme, the card's
             own dark elsewhere — a token, never a literal). */
          counterBg="var(--bc)"
          nid={nid}
          tkTypes={tkTypes}
          canTerms={_canSeeOwnTimeFinancial}
          canEditTime={true}
          canMarkBilled={_permIsAdmin}
          /* 150 §5 (d)+(g) — unscoped: admin or a role default; a per-case
             grant cannot mean anything on a spans-matters population. */
          canImportExport={_perms.can("caseView.timeEntries.importExport")}
          canPillColumn={_perms.can("caseView.timeEntries.pillColumn")}
          _roleRows={_roleRows}
          openTimeModal={(k: string, p: Record<string, unknown>) => om(k, p)}
          onDelete={delTeEntry}
          sCol={() => !teP.open}
          tog={() => setTeP((x: any) => ({ ...x, open: !x.open }))}
          initialUserFilter={currentUser || null}
          initialPeriod={{ kind: "week" }}
          showCaseFilter={true}
          t={tr}
          toast={toast}
        />
        {teDelUndo && (
          <div
            key={teDelUndo.id}
            style={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
              padding: "7px 10px 10px",
              background: "var(--rd)",
              borderRadius: RAD.r8,
              fontSize: FS.s11,
            }}
          >
            <span
              style={{
                flex: 1,
                color: CT.c_fff,
                fontSize: FS.s10_5,
                fontWeight: 500,
              }}
            >
              {tr("dash.te.deleted")}
            </span>
            <button
              type="button"
              style={{
                padding: "2px 10px",
                fontSize: FS.s10,
                background: "none",
                border: "1.5px solid rgba(255,255,255,.8)",
                color: CT.c_fff,
                borderRadius: RAD.r6,
                cursor: "pointer",
                fontWeight: 600,
                flexShrink: 0,
                fontFamily: "inherit",
              }}
              onClick={undoTeEntry}
            >
              {tr("common.undo")}
            </button>
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                height: 3,
                background: "rgba(255,255,255,.45)",
                animation: `ud ${String(UNDO_WINDOW_MS)}ms linear forwards`,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Dash;
